import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  KeyRound, Plus, Trash2, Eye, EyeOff, Loader2, Save,
  CheckCircle2, AlertTriangle, GripVertical, RefreshCw,
} from "lucide-react";
import { platformApi } from "../../lib/api";

/** Platform-wide demo login tiles for the public Login page. Super-admin
 *  toggles the section, adds/removes tiles, and password changes flow
 *  through to the underlying user's password hash so "Use →" actually
 *  signs in. Non-existent emails are flagged (they still save but the
 *  public Login page will filter them out). */
export default function DemoAccountsPanel() {
  const [enabled, setEnabled] = useState(false);
  const [rows, setRows] = useState([]);
  const [statusByEmail, setStatusByEmail] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const cfg = await platformApi.getDemoAccounts();
      setEnabled(!!cfg.enabled);
      setRows(
        (cfg.accounts || []).map((a) => ({
          _uid: a.id,
          id: a.id,
          label: a.label || "",
          email: a.email || "",
          password: a.password || "",
          role: a.role || "admin",
          reveal: false,
          hadPassword: !!a.password,
        })),
      );
      setStatusByEmail({});
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load demo accounts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const patch = (uid, partial) => {
    setRows((rs) => rs.map((r) => (r._uid === uid ? { ...r, ...partial } : r)));
    setDirty(true);
  };
  const removeRow = (uid) => {
    setRows((rs) => rs.filter((r) => r._uid !== uid));
    setDirty(true);
  };
  const addRow = () => {
    if (rows.length >= 8) { toast.error("Maximum 8 demo tiles"); return; }
    setRows((rs) => [...rs, {
      _uid: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      id: null, label: "", email: "", password: "", role: "admin",
      reveal: true, hadPassword: false,
    }]);
    setDirty(true);
  };
  const move = (uid, dir) => {
    setRows((rs) => {
      const idx = rs.findIndex((r) => r._uid === uid);
      if (idx < 0) return rs;
      const j = idx + dir;
      if (j < 0 || j >= rs.length) return rs;
      const copy = [...rs];
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return copy;
    });
    setDirty(true);
  };
  const toggleEnabled = (v) => { setEnabled(v); setDirty(true); };

  const save = async () => {
    const clean = rows
      .map((r) => ({
        id: r.id || undefined,
        label: (r.label || "").trim(),
        email: (r.email || "").trim().toLowerCase(),
        password: r.password || "",
        role: r.role || "admin",
      }))
      .filter((r) => r.email);
    setSaving(true);
    try {
      const res = await platformApi.saveDemoAccounts({ enabled, accounts: clean });
      const status = {};
      (res.results || []).forEach((r) => { status[r.email] = r.status; });
      setStatusByEmail(status);
      // Reload to pick up server-assigned ids
      const cfg = { enabled: res.enabled, accounts: res.accounts };
      setEnabled(!!cfg.enabled);
      setRows(
        (cfg.accounts || []).map((a) => ({
          _uid: a.id, id: a.id, label: a.label || "", email: a.email || "",
          password: a.password || "", role: a.role || "admin",
          reveal: false, hadPassword: !!a.password,
        })),
      );
      setDirty(false);
      toast.success("Demo accounts saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="demo-accounts-panel">
        <Loader2 className="h-6 w-6 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="demo-accounts-panel">
      {/* Header + master toggle */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <span className="h-10 w-10 rounded-lg bg-cyan-500/15 text-cyan-300 flex items-center justify-center shrink-0">
              <KeyRound className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-[15px] font-semibold text-slate-100">Login page demo tiles</h2>
              <p className="text-[12.5px] text-slate-400 mt-0.5 max-w-xl">
                When enabled, the public Login page shows quick-fill tiles below. Each tile
                provisions a real user in the shared <span className="text-slate-200 font-medium">Demo Workspace</span>,
                so visitors can click &quot;Use →&quot; and sign in — no signup, no risk to
                your real data.
              </p>
            </div>
          </div>
          <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
            <span className="text-[12.5px] text-slate-300">{enabled ? "On" : "Off"}</span>
            <span
              role="switch"
              aria-checked={enabled}
              tabIndex={0}
              onClick={() => toggleEnabled(!enabled)}
              onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggleEnabled(!enabled); } }}
              data-testid="demo-accounts-enabled-switch"
              className={`w-10 h-6 rounded-full relative transition-colors ${enabled ? "bg-cyan-500" : "bg-slate-600"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${enabled ? "left-[18px]" : "left-0.5"}`} />
            </span>
          </label>
        </div>
      </div>

      {/* Rows */}
      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-4 py-2.5 border-b border-white/10 bg-white/[0.02] flex items-center justify-between">
          <div className="text-[12px] uppercase tracking-wider font-semibold text-slate-400">
            {rows.length} of 8 tiles
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={saving || loading}
              data-testid="demo-accounts-reload"
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 text-[12px] disabled:opacity-50"
            >
              <RefreshCw className="h-3 w-3" /> Reload
            </button>
            <button
              type="button"
              onClick={addRow}
              disabled={rows.length >= 8}
              data-testid="demo-accounts-add"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-cyan-500/20 border border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/30 text-[12px] font-semibold disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" /> Add tile
            </button>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-[13px]">
            No demo tiles yet. Click <span className="text-slate-300">Add tile</span> to create the first one.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {rows.map((r, i) => {
              const status = statusByEmail[r.email];
              const isValid = /@/.test(r.email);
              return (
                <div key={r._uid} className="p-3 flex items-start gap-2" data-testid={`demo-row-${i}`}>
                  <div className="flex flex-col items-center justify-start gap-1 pt-1">
                    <button
                      type="button"
                      onClick={() => move(r._uid, -1)}
                      disabled={i === 0}
                      className="text-slate-500 hover:text-slate-200 disabled:opacity-30 text-[9px] leading-none"
                      title="Move up"
                    >▲</button>
                    <GripVertical className="h-3.5 w-3.5 text-slate-600" />
                    <button
                      type="button"
                      onClick={() => move(r._uid, 1)}
                      disabled={i === rows.length - 1}
                      className="text-slate-500 hover:text-slate-200 disabled:opacity-30 text-[9px] leading-none"
                      title="Move down"
                    >▼</button>
                  </div>
                  <div className="flex-1 grid grid-cols-12 gap-2">
                    <input
                      value={r.label}
                      onChange={(e) => patch(r._uid, { label: e.target.value })}
                      placeholder="Label (e.g. Admin)"
                      data-testid={`demo-row-label-${i}`}
                      className="col-span-3 h-9 px-2.5 rounded-md bg-white/5 border border-white/10 text-[13px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
                    />
                    <input
                      value={r.email}
                      onChange={(e) => patch(r._uid, { email: e.target.value })}
                      placeholder="email@company.com"
                      data-testid={`demo-row-email-${i}`}
                      className="col-span-3 h-9 px-2.5 rounded-md bg-white/5 border border-white/10 text-[13px] text-slate-100 placeholder:text-slate-500 font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
                    />
                    <select
                      value={r.role}
                      onChange={(e) => patch(r._uid, { role: e.target.value })}
                      data-testid={`demo-row-role-${i}`}
                      className="col-span-2 h-9 px-2 rounded-md bg-white/5 border border-white/10 text-[13px] text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
                    >
                      <option value="admin">Admin</option>
                      <option value="dispatcher">Dispatcher</option>
                      <option value="technician">Technician</option>
                    </select>
                    <div className="col-span-4 relative">
                      <input
                        type={r.reveal ? "text" : "password"}
                        value={r.password}
                        onChange={(e) => patch(r._uid, { password: e.target.value })}
                        placeholder={r.hadPassword ? "•••• (leave blank to keep)" : "Set a password"}
                        data-testid={`demo-row-password-${i}`}
                        className="w-full h-9 pl-2.5 pr-16 rounded-md bg-white/5 border border-white/10 text-[13px] text-slate-100 placeholder:text-slate-500 font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
                      />
                      <button
                        type="button"
                        onClick={() => patch(r._uid, { reveal: !r.reveal })}
                        data-testid={`demo-row-reveal-${i}`}
                        className="absolute right-8 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200"
                        title={r.reveal ? "Hide" : "Show"}
                      >
                        {r.reveal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeRow(r._uid)}
                        data-testid={`demo-row-remove-${i}`}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-rose-300"
                        title="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {/* Row footer: status + hint */}
                    {(status || (!isValid && r.email)) && (
                      <div className="col-span-12 text-[11px] flex items-center gap-1.5 pl-0.5">
                        {status === "user_created" && (
                          <span className="inline-flex items-center gap-1 text-emerald-300">
                            <CheckCircle2 className="h-3 w-3" /> Demo user created — ready to sign in
                          </span>
                        )}
                        {status === "user_updated" && (
                          <span className="inline-flex items-center gap-1 text-emerald-300">
                            <CheckCircle2 className="h-3 w-3" /> Demo user updated
                          </span>
                        )}
                        {status === "email_taken" && (
                          <span className="inline-flex items-center gap-1 text-rose-300">
                            <AlertTriangle className="h-3 w-3" /> Email is used by a real user — pick a different email for the demo
                          </span>
                        )}
                        {status === "missing_password" && (
                          <span className="inline-flex items-center gap-1 text-amber-300">
                            <AlertTriangle className="h-3 w-3" /> Password required
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sticky save */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12px] text-slate-500">
          Passwords are hashed into the demo user&apos;s credentials. Remove a tile
          to delete its demo user. Real (non-demo) users are never touched.
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid="demo-accounts-save"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save changes
        </button>
      </div>
    </div>
  );
}
